import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Loader2, Repeat2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import ReplaceSession from "@/components/creator/replace/ReplaceSession";
import ReplaceSidebar from "@/components/creator/replace/ReplaceSidebar";
import VersionPills, { sortedGenerated } from "@/components/creator/sing/VersionPills";
import { getProject } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

/** "Replace Lead Vocal" — swap a version's AI lead for one of your own takes, with noise cleanup + pitch correction. */
export default function ReplaceVocalPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try { setProject(await getProject(id)); } catch (e) { setError(e?.response?.status === 404 ? "We couldn't find that project." : safeApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const generated = sortedGenerated(project?.versions);
  const version = generated.find((v) => v.id === params.get("version")) || generated[generated.length - 1];
  const back = `/create/project/${id}`;
  if (error) return <CreatorShell testId="replace-page" back="/create/projects" title="Hmm, that project isn't here" subtitle={error} />;
  if (!project) return <CreatorShell testId="replace-page" back={back}><p className="flex items-center gap-2 text-white/60"><Loader2 className="h-5 w-5 animate-spin" /> Opening your project…</p></CreatorShell>;
  if (!version) {
    return (
      <CreatorShell testId="replace-page" back={back} title="Make the song first" subtitle="Generate a version of this project, then come back to put your voice on it.">
        <Link to={back} className="inline-flex rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-3 font-display font-semibold">Back to the project</Link>
      </CreatorShell>
    );
  }
  return (
    <CreatorShell testId="replace-page" back={back} spine="record" spineDone={["write", "generate"]} wide>
      <div className="mb-8">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-amber-300/90"><Repeat2 className="h-3.5 w-3.5" /> Replace lead vocal</p>
        <h1 data-testid="replace-title" className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">{project.title || "Song"}</h1>
        <p data-testid="replace-subtitle" className="mt-2 text-white/60">Version {version.take} · {project.arrangement_label} — your voice in, the AI lead out.</p>
      </div>
      <VersionPills versions={generated} current={version} onPick={(v) => setParams({ version: v }, { replace: true })} label="Replace on" testId="replace-version" tone="amber" />
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0">
          <ReplaceSession key={version.id} project={project} version={version} initialTakeId={params.get("take")} onProject={setProject} />
        </div>
        <ReplaceSidebar project={project} version={version} />
      </div>
    </CreatorShell>
  );
}
